import { Component, DestroyRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { SbLocalDateTime } from '../../../../services/api/api-date-time';
import { DateTime } from 'luxon';
import {
  API,
  CampaignWithPreviewDTO,
  LocalizedFramedReportingDTO,
  PlayoutAggregationDTO,
  TimingStatus,
} from '../../../../services/api/api';
import { ErrorService } from '../../../../services/error.service';
import { UtilService } from '../../../../services/util.service';
import { XYChartModel } from '@ngx-seatback/charts';
import { ChartComponent } from '@ngx-seatback/charts';
import { PermissionService } from '../../../../services/permission.service';
import { of, Subscription, switchMap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take } from 'rxjs/operators';

@Component({
  selector: 'sb-playouts-overall',
  templateUrl: './playouts-overall.component.html',
  styleUrls: ['./playouts-overall.component.scss'],
  standalone: false,
})
export class PlayoutsOverallComponent implements OnInit, OnDestroy {
  @ViewChild('chart') chart: ChartComponent;
  campaigns: CampaignWithPreviewDTO[];
  lineChartGradient: XYChartModel;
  selTiming: TimingStatus = null;
  loading = true;

  subs: Subscription[] = [];

  constructor(
    public errorHandler: ErrorService,
    private api: API,
    public util: UtilService,
    public permissionService: PermissionService,
    private destroyRef: DestroyRef
  ) {}

  ngOnInit(): void {
    this.lineChartGradient = {
      title: 'Playout Statistics',
      height: 150,
      valueAssignments: [
        {
          value: 'value',
          categories: ['day'],
          valueName: 'Playouts',
          color: '#016aa2',
        },
      ],
      fill: true,
      gradient: true,
      data: [],
    };
    this.selTiming = TimingStatus.Present;
    if (this.permissionService.hasPermission(this.permissionService.Permission.campaign_list) && !this.lineChartGradient?.loadingDone) {
      this.subs.forEach(sub => sub.unsubscribe());
      this.subs = [];
      const sub = this.api
        .ds_cm_Campaign_retrieveAllFrontendEnhanced({
          timingStatus: this.selTiming ? [this.selTiming] : null,
          company: null,
        })
        .pipe(
          switchMap(data => {
            this.campaigns = data;

            const from = new SbLocalDateTime(DateTime.fromJSDate(this.util.getFromDate(7)));
            const till = new SbLocalDateTime(DateTime.local());

            const campaignIds: number[] = [];
            this.campaigns.forEach(x => {
              campaignIds.push(x.id);
            });
            if (campaignIds.length > 0) {
              return this.api.ds_cm_Reporting_campaignsReport({
                id: campaignIds,
                interval: 86400,
                from,
                till,
              });
            } else {
              return of(null);
            }
          }),
          takeUntilDestroyed(this.destroyRef),
          take(1)
        )
        .subscribe({
          next: (reporting: LocalizedFramedReportingDTO) => {
            if (reporting) {
              const dates = {};
              for (let count = 0; count < 7; count++) {
                const valueDate = DateTime.local()
                  .minus({ days: 7 - count })
                  .toFormat('LLL dd');
                dates[valueDate] = 0;
              }
              const chartData = [];
              reporting.buckets.forEach(y => {
                y.frames.forEach((x, i) => {
                  const frame = x.data as PlayoutAggregationDTO;
                  const valueDate = DateTime.local()
                    .minus({ days: 7 - i })
                    .toFormat('LLL dd');
                  dates[valueDate] += frame.productivePlayouts;
                });
              });

              for (const valueDate in dates) {
                if (valueDate) {
                  chartData.push({
                    day: valueDate,
                    value: dates[valueDate],
                  });
                }
              }
              this.chart.reloadData(chartData);
              this.lineChartGradient.loadingDone = true;
            }
            this.loading = false;
          },
          error: error => {
            this.errorHandler.errorHandler(error);
            this.loading = false;
          },
        });
      this.subs.push(sub);
    }
  }

  ngOnDestroy(): void {
    this.subs.forEach(sub => sub.unsubscribe());
    this.subs = [];
  }
}
